import clsx from 'clsx';
import React from 'react';

import { CarretArrowDown } from '../../../atoms/icons';
import { Text } from '../../../typography/text/Text';
import Media from '../../media';
import { headerHeight, theme } from './_vars';
import styles from './JournalBanner.module.scss';

// Define interfaces for the component props
interface MediaDevice {
  url: string;
  title?: string;
  type?: string;
  contentType?: string;
  width?: number;
  height?: number;
}

interface Media {
  showControls?: boolean;
  muted?: boolean;
  desktopCloudinary?: string;
  mobileCloudinary?: string;
  tabletCloudinary?: string;
  desktop?: MediaDevice;
  mobile?: MediaDevice;
  tablet?: MediaDevice;
}

export interface Banner {
  subtitle?: string;
  title?: string;
  description?: string;
  media?: Media | null;
  hasScrollBottomButton?: boolean;
  fullWidth?: boolean;
}

interface JournalBannerProps {
  banner: Banner;
  isSSR: boolean;
}

const JournalBanner: React.FC<JournalBannerProps> = ({ banner, isSSR }) => {
  const { subtitle, title, description, media = null, hasScrollBottomButton = false, fullWidth } = banner;

  const hasMedia = media ? Object.values(media).some((img) => (img as MediaDevice)?.url) : false;

  if (!hasMedia) {
    return null;
  }

  const handleScrollBottom = () => {
    if (!isSSR) {
      const body = document?.getElementById('bodyWrapper');
      const banner = document?.getElementById('banner');
      const position = banner?.getBoundingClientRect();
      body?.scroll({
        top: body?.scrollTop + position?.bottom - headerHeight,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={clsx(styles.container, { [`${styles.fullWith}`]: fullWidth })}>
      <div id="banner" className={styles.banner}>
        <Media
          isSSR={isSSR}
          media={media}
          showControls={media?.showControls}
          muted={media?.muted}
          wrappedContent
          customClasses={{
            media: styles.media,
            controls: styles.controlsWrapper,
          }}
        />
        <div className={styles.content}>
          <div className={styles.titleBlock}>
            {subtitle && (
              <Text className={styles.subtitle} as="span" textTransform={theme === 'saba' ? 'uppercase' : 'initial'}>
                {subtitle}
              </Text>
            )}
            {title && (
              <Text as="h2" className={styles.title} weight={theme === 'jag' ? 'semibold' : 'light'}>
                {title}
              </Text>
            )}
          </div>
          <div className={styles.bottomBlock}>
            {description && (
              <Text
                className={clsx(styles.description, {
                  [`${styles.hasPaddings}`]: !hasScrollBottomButton,
                })}
                size="sm"
                as="p"
              >
                {description}
              </Text>
            )}
            {hasScrollBottomButton && (
              <div className={styles.scrollButton} onClick={handleScrollBottom}>
                <CarretArrowDown className={styles.icon} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalBanner;
